import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './RegisterModal.module.css';
import logoFull from '../../../../assets/images/logo-full.png';
import { RegisterAPI, LoginAPI } from '../../../../api/API';
import { setAccessToken } from '../../../../api/authTokens';
import { clearReferralCode } from '../../../../utils/referralCode';
import {
  trackCtaClick,
  trackFormStart,
  trackFormError,
  trackSignUp,
  trackSignupStarted,
  trackSignupSubmitted,
  trackVerificationEmailSent
} from '../../../../utils/analytics';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import VisibilityOnIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';

// DDI padrão do telefone (Brasil); a pessoa pode trocar no campo ao lado do WhatsApp
const DEFAULT_COUNTRY_CODE = '55';

function RegisterModal({ isOpen, onClose, referrerName = null }) {
  const navigate = useNavigate();
  // Estado para controlar o formulário completo
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    countryCode: DEFAULT_COUNTRY_CODE,
    whatsapp: '',
    companyName: '',
    password: '',
    confirmPassword: ''
  });

  // Mostrar/ocultar a senha (vale para os dois campos de senha)
  const [showPassword, setShowPassword] = useState(false);

  // Estado para controlar o status do envio do formulário
  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    success: false,
    error: null,
    message: null
  });

  // Estado para controlar erros de validação
  const [validationErrors, setValidationErrors] = useState({
    email: null,
    fullName: null,
    companyName: null,
    password: null,
    confirmPassword: null
  });

  // Ref para o primeiro input (foco automático ao abrir)
  const firstInputRef = useRef(null);

  // Efeito para bloquear scroll do body quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Foco automático no primeiro input após animação
      const timer = setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Efeito para fechar com ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Resetar formulário ao fechar
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        fullName: '',
        email: '',
        countryCode: DEFAULT_COUNTRY_CODE,
        whatsapp: '',
        companyName: '',
        password: '',
        confirmPassword: ''
      });
      setShowPassword(false);
      setSubmitStatus({ loading: false, success: false, error: null, message: null });
      setValidationErrors({ email: null, fullName: null, companyName: null, password: null, confirmPassword: null });
    }
  }, [isOpen]);

  // Função para validar email
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return 'Informe o e-mail profissional';
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email.trim())) {
      return 'Digite um e-mail válido (ex.: voce@clinica.com.br)';
    }
    return null;
  };

  // Função para validar nome completo
  const validateFullName = (name) => {
    if (!name || name.trim() === '') {
      return 'Informe seu nome completo';
    }
    if (name.trim().length < 3) {
      return 'Mínimo de 3 caracteres';
    }
    return null;
  };

  // Função para validar nome da empresa
  const validateCompanyName = (name) => {
    if (!name || name.trim() === '') {
      return 'Informe o nome da clínica ou consultório';
    }
    if (name.trim().length < 3) {
      return 'O nome da clínica precisa ter pelo menos 3 caracteres';
    }
    return null;
  };

  // Função para validar senha
  const validatePassword = (password) => {
    if (!password || password.trim() === '') {
      return 'Informe a senha';
    }
    if (password.length < 8) {
      return 'Mínimo de 8 caracteres';
    }
    return null;
  };

  // Função para validar confirmação de senha
  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword || confirmPassword.trim() === '') {
      return 'Repita a senha';
    }
    if (password !== confirmPassword) {
      return 'As senhas não são iguais';
    }
    return null;
  };

  // Função para aplicar máscara no WhatsApp (a máscara (11) 99999-9999 só vale para o Brasil)
  const applyWhatsAppMask = (value, countryCode = formData.countryCode) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) {
      return value;
    }
    if (countryCode !== DEFAULT_COUNTRY_CODE) {
      return numbers.slice(0, 15);
    }
    if (numbers.length <= 2) {
      return `(${numbers}`;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // Função para lidar com mudanças nos campos do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    if (name === 'whatsapp') {
      processedValue = applyWhatsAppMask(value);
    }

    if (name === 'countryCode') {
      processedValue = value.replace(/\D/g, '').slice(0, 4);
      // Refaz a máscara do número para o DDI escolhido
      setFormData({
        ...formData,
        countryCode: processedValue,
        whatsapp: applyWhatsAppMask(formData.whatsapp, processedValue)
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: processedValue
    });

    let fieldError = null;

    switch (name) {
      case 'email':
        fieldError = validateEmail(value);
        break;
      case 'fullName':
        fieldError = validateFullName(value);
        break;
      case 'companyName':
        fieldError = validateCompanyName(value);
        break;
      case 'password':
        fieldError = validatePassword(value);
        if (formData.confirmPassword) {
          const confirmError = validateConfirmPassword(value, formData.confirmPassword);
          setValidationErrors(prev => ({
            ...prev,
            confirmPassword: confirmError
          }));
        }
        break;
      case 'confirmPassword':
        fieldError = validateConfirmPassword(formData.password, value);
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
  };

  // Função para lidar com o envio do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {
      fullName: validateFullName(formData.fullName),
      email: validateEmail(formData.email),
      companyName: validateCompanyName(formData.companyName),
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(formData.password, formData.confirmPassword)
    };

    const hasErrors = Object.values(errors).some(error => error !== null);

    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }

    setSubmitStatus({
      loading: true,
      success: false,
      error: null,
      message: null
    });

    const registrationData = {
      name: formData.fullName,
      email: formData.email,
      phone: formData.whatsapp,
      countryCode: formData.countryCode || DEFAULT_COUNTRY_CODE,
      companyName: formData.companyName,
      password: formData.password,
      signupOption: 'create'
    };

    const loginEmail = formData.email;
    const loginPassword = formData.password;

    trackSignupSubmitted({ method: 'landing_page_direct' });

    RegisterAPI(registrationData, (response) => {
      if (response.success) {
        // O código de indicação já foi junto com o cadastro
        clearReferralCode();

        setFormData({
          fullName: '',
          email: '',
          countryCode: DEFAULT_COUNTRY_CODE,
          whatsapp: '',
          companyName: '',
          password: '',
          confirmPassword: ''
        });

        trackSignUp({
          method: 'landing_page_direct',
          success: true,
          source: 'landing_page'
        });

        // A conversão do Google Ads sai na confirmação do e-mail (PendingVerification
        // e ConfirmEmail), quando a conta de fato nasce, não aqui.

        // Cadastro agora exige confirmação de e-mail: nenhuma sessão é criada pelo backend
        // (auth/register.php retorna status: 'pending_verification', sem token/session).
        if (response.status === 'pending_verification') {
          trackVerificationEmailSent({ method: 'landing_page_direct' });

          // status_key deixa a tela "Confirme seu e-mail" perceber a confirmação sozinha
          const statusKey = response.status_key || '';
          try {
            sessionStorage.setItem('pendingVerificationEmail', loginEmail);
            sessionStorage.setItem('pendingVerificationStatusKey', statusKey);
          } catch (e) {
            // sessionStorage indisponível - segue sem bloquear o redirect
          }

          onClose();
          navigate('/confirme-seu-email', { state: { email: loginEmail, statusKey } });
          return;
        }

        // Fallback de compatibilidade: caso o backend responda sucesso sem o campo
        // `status` (comportamento antigo, sem verificação de e-mail), mantém o
        // login automático para não quebrar ambientes ainda não atualizados.
        setSubmitStatus({
          loading: false,
          success: true,
          error: null,
          message: 'Conta criada com sucesso! Entrando na sua conta...'
        });

        LoginAPI(loginEmail, loginPassword, (loginResponse) => {
          if (loginResponse && loginResponse.token) {
            // O refresh token ficou no cookie HttpOnly. A sessão salva faz o boot do
            // App renovar o access token pelo cookie depois do redirecionamento.
            setAccessToken(loginResponse.token);
            if (loginResponse.session) {
              localStorage.setItem('sessionData', JSON.stringify(loginResponse.session));
            }
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 1500);
          } else {
            setSubmitStatus({
              loading: false,
              success: true,
              error: null,
              message: 'Conta criada com sucesso! Faça login para continuar.'
            });
            setTimeout(() => {
              window.location.href = '/login';
            }, 2500);
          }
        });
      } else {

        trackFormError({
          formName: 'direct_signup',
          field: 'api',
          errorCode: response.error || 'registration_failed'
        });

        const details = Array.isArray(response.details) ? response.details.filter(Boolean) : [];
        const serverMessage = details.length > 0
          ? details.join('. ')
          : (response.message || (typeof response.error === 'string' && response.error.includes(' ') ? response.error : null));

        if (details.length > 0) {
          setValidationErrors(prev => ({
            ...prev,
            password: details.join('. ')
          }));
        }

        setSubmitStatus({
          loading: false,
          success: false,
          error: response.error || 'generic_error',
          message: serverMessage || 'Ocorreu um erro ao criar sua conta. Por favor, tente novamente.'
        });
      }
    });
  };

  // Fechar ao clicar no backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`${styles.backdrop} ${isOpen ? styles.backdropVisible : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Criar conta grátis"
    >
      <div className={`${styles.modal} ${isOpen ? styles.modalVisible : ''}`}>
        {/* Botão fechar */}
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Fechar modal"
        >
          <CloseIcon />
        </button>

        {/* Layout de duas colunas */}
        <div className={styles.modalLayout}>
          {/* Lado esquerdo — visual/brand */}
          <div className={styles.leftPanel}>
            <div className={styles.leftContent}>
              <img
                src={logoFull}
                alt="Gabriela"
                className={styles.logo}
              />

              <h2 className={styles.leftTitle}>
                Sua rotina mais organizada começa aqui
              </h2>

              <p className={styles.leftSubtitle}>
                Agenda, pacientes, documentos, financeiro e comunicação em um só lugar.
              </p>

              {referrerName && (
                <p className={styles.referrerNote}>
                  Indicação de <strong>{referrerName}</strong>
                </p>
              )}

              <ul className={styles.benefitsList}>
                <li className={styles.benefitItem}>
                  <span className={styles.benefitCheck}><CheckRoundedIcon fontSize="inherit" /></span>
                  <span>30 dias grátis para testar</span>
                </li>
                <li className={styles.benefitItem}>
                  <span className={styles.benefitCheck}><CheckRoundedIcon fontSize="inherit" /></span>
                  <span>Sem fidelidade</span>
                </li>
                <li className={styles.benefitItem}>
                  <span className={styles.benefitCheck}><CheckRoundedIcon fontSize="inherit" /></span>
                  <span>Cancelamento fácil</span>
                </li>
                <li className={styles.benefitItem}>
                  <span className={styles.benefitCheck}><CheckRoundedIcon fontSize="inherit" /></span>
                  <span>Suporte incluso</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Lado direito — formulário */}
          <div className={styles.rightPanel}>
            <div className={styles.formHeader}>
              <h3 className={styles.formTitle}>Criar minha conta grátis</h3>
              <p className={styles.formSubtitle}>Leva menos de 2 minutos</p>
            </div>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              {/* Linha 1: Nome + WhatsApp */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="modal-fullName" className={styles.label}>
                    Nome completo
                  </label>
                  <input
                    ref={firstInputRef}
                    type="text"
                    id="modal-fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    onFocus={() => {
                      trackFormStart({ formName: 'direct_signup_modal' });
                      trackSignupStarted({ method: 'landing_page_direct' });
                    }}
                    placeholder="Seu nome completo"
                    className={`${styles.input} ${validationErrors.fullName ? styles.inputError : ''}`}
                    autoComplete="name"
                  />
                  <span className={styles.fieldError} role={validationErrors.fullName ? 'alert' : undefined}>{validationErrors.fullName}</span>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="modal-whatsapp" className={styles.label}>
                    WhatsApp
                  </label>
                  <div className={styles.phoneRow}>
                    <div className={styles.countryCodeField}>
                      <span className={styles.countryCodePrefix} aria-hidden="true">+</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        id="modal-countryCode"
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        aria-label="DDI do país"
                        title="DDI do país (55 = Brasil)"
                        className={`${styles.input} ${styles.countryCodeInput}`}
                        autoComplete="tel-country-code"
                      />
                    </div>
                    <input
                      type="text"
                      inputMode="tel"
                      id="modal-whatsapp"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      placeholder={formData.countryCode === DEFAULT_COUNTRY_CODE ? '(11) 99999-9999' : 'Número com DDD'}
                      className={styles.input}
                      autoComplete="tel-national"
                    />
                  </div>
                  {/* Sem mensagem de erro: a linha vazia mantém o espaçamento dos outros campos */}
                  <span className={styles.fieldError} aria-hidden="true" />
                </div>
              </div>

              {/* Linha 2: E-mail */}
              <div className={styles.formGroup}>
                <label htmlFor="modal-email" className={styles.label}>
                  E-mail profissional
                </label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="seu@email.com"
                  className={`${styles.input} ${validationErrors.email ? styles.inputError : ''}`}
                  autoComplete="email"
                />
                <span className={styles.fieldError} role={validationErrors.email ? 'alert' : undefined}>{validationErrors.email}</span>
              </div>

              {/* Linha 3: Nome da clínica */}
              <div className={styles.formGroup}>
                <label htmlFor="modal-companyName" className={styles.label}>
                  Nome da clínica/consultório
                </label>
                <input
                  type="text"
                  id="modal-companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Ex: Clínica Dr. Silva"
                  className={`${styles.input} ${validationErrors.companyName ? styles.inputError : ''}`}
                  autoComplete="organization"
                />
                <span className={styles.fieldError} role={validationErrors.companyName ? 'alert' : undefined}>{validationErrors.companyName}</span>
              </div>

              {/* Linha 4: Senha + Confirmar senha */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="modal-password" className={styles.label}>
                    Senha
                  </label>
                  <div className={styles.passwordField}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="modal-password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Mínimo 8 caracteres"
                      className={`${styles.input} ${styles.passwordInput} ${validationErrors.password ? styles.inputError : ''}`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityOnIcon />}
                    </button>
                  </div>
                  <span className={styles.fieldError} role={validationErrors.password ? 'alert' : undefined}>{validationErrors.password}</span>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="modal-confirmPassword" className={styles.label}>
                    Confirmar senha
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="modal-confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Repita a senha"
                    className={`${styles.input} ${validationErrors.confirmPassword ? styles.inputError : ''}`}
                    autoComplete="new-password"
                  />
                  <span className={styles.fieldError} role={validationErrors.confirmPassword ? 'alert' : undefined}>{validationErrors.confirmPassword}</span>
                </div>
              </div>

              {/* Mensagem de erro da API */}
              {submitStatus.message && submitStatus.error && (
                <div className={styles.errorMessage}>
                  {submitStatus.message}
                </div>
              )}

              {/* Botão CTA */}
              <button
                type="submit"
                className={styles.submitButton}
                disabled={submitStatus.loading}
                onClick={() => !submitStatus.loading && trackCtaClick({ placement: 'register_modal_form' })}
              >
                {submitStatus.loading ? 'Criando conta...' : 'Começar gratuitamente'}
              </button>

              {/* Aceite dos termos: gravado no backend quando a conta é criada (confirmação do e-mail) */}
              <p className={styles.termsNote}>
                Ao criar a conta você concorda com os{' '}
                <a href="/terms-privacy" target="_blank" rel="noopener noreferrer">Termos de Uso</a>
                {' '}e a{' '}
                <a href="/terms-privacy" target="_blank" rel="noopener noreferrer">Política de Privacidade</a>.
              </p>

              {/* Microcopy */}
              <div className={styles.microcopy}>
                <p>30 dias grátis para testar todas as funcionalidades.</p>
                <p>Não é necessário cartão de crédito para ativar sua conta.</p>
              </div>
            </form>
          </div>
        </div>

        {/* Popup de sucesso (overlay interno) */}
        {submitStatus.success && (
          <div className={styles.successOverlay}>
            <div className={styles.successContainer}>
              <div className={styles.successIcon}>
                <CheckCircleOutlineIcon className={styles.successIconMui} />
              </div>

              <h2 className={styles.successTitle}>
                Sua conta foi criada com sucesso!
              </h2>

              <p className={styles.successMessage}>
                Bem-vindo à Gabriela. Agora você tem em um só lugar tudo o que precisa para organizar sua rotina.
              </p>

              <div className={styles.successButtons}>
                <button
                  className={styles.successPrimaryButton}
                  onClick={() => window.location.href = '/dashboard'}
                >
                  Acessar minha conta
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RegisterModal;
